import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, TrendingUp, Clock, XCircle, AlertCircle } from 'lucide-react';
import { getAuthHeaders } from '../lib/auth';
import Navigation from '../components/Navigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventTypeAnalytics = () => {
  const { eventTypeId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [eventType, setEventType] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    loadData();
  }, [eventTypeId]);

  const loadData = async () => {
    try {
      const [eventTypeRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/event-types/${eventTypeId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/event-types/${eventTypeId}/analytics`, { headers: getAuthHeaders() })
      ]);
      
      setEventType(eventTypeRes.data.eventType);
      setAnalytics(analyticsRes.data.analytics);
    } catch (error) {
      toast.error('Failed to load analytics');
      navigate('/event-types');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto"></div>
            <p className="mt-4 text-slate-600">Loading analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  const completionRate = analytics.totalBookings > 0 
    ? Math.round((analytics.completedBookings / analytics.totalBookings) * 100) 
    : 0;
  
  const cancellationRate = analytics.totalBookings > 0
    ? Math.round((analytics.cancelledBookings / analytics.totalBookings) * 100)
    : 0;
  
  const noShowRate = analytics.totalBookings > 0
    ? Math.round((analytics.noShowBookings / analytics.totalBookings) * 100)
    : 0;

  const sortedPopularTimes = Object.entries(analytics.popularTimes || {})
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/event-types')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Event Types
        </Button>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: eventType.color }}
            />
            <h1 className="text-4xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {eventType.name}
            </h1>
          </div>
          <p className="text-slate-600">Analytics & Insights</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-600 text-sm">Total Bookings</span>
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-slate-900">{analytics.totalBookings}</p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-600 text-sm">Completed</span>
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-slate-900">{analytics.completedBookings}</p>
            <p className="text-sm text-slate-500 mt-1">{completionRate}% completion rate</p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-600 text-sm">Cancelled</span>
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <p className="text-3xl font-bold text-slate-900">{analytics.cancelledBookings}</p>
            <p className="text-sm text-slate-500 mt-1">{cancellationRate}% cancellation rate</p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-600 text-sm">No Shows</span>
              <AlertCircle className="w-5 h-5 text-orange-600" />
            </div>
            <p className="text-3xl font-bold text-slate-900">{analytics.noShowBookings}</p>
            <p className="text-sm text-slate-500 mt-1">{noShowRate}% no-show rate</p>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Popular Times */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <Clock className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-semibold">Popular Time Slots</h2>
            </div>
            
            {sortedPopularTimes.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No booking data yet</p>
            ) : (
              <div className="space-y-4">
                {sortedPopularTimes.map(([time, count], index) => (
                  <div key={time} className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{time}</p>
                      <div className="mt-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-600 rounded-full"
                          style={{
                            width: `${(count / sortedPopularTimes[0][1]) * 100}%`
                          }}
                        />
                      </div>
                    </div>
                    <span className="text-sm text-slate-600">{count} bookings</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Lead Time */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <h2 className="text-xl font-semibold">Booking Insights</h2>
            </div>
            
            <div className="space-y-6">
              <div>
                <p className="text-sm text-slate-600 mb-2">Average Lead Time</p>
                <p className="text-3xl font-bold text-slate-900">
                  {Math.round(analytics.averageLeadTime)} hours
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  Average time between booking and meeting
                </p>
              </div>

              <div className="pt-6 border-t">
                <p className="text-sm text-slate-600 mb-4">Booking Status Distribution</p>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Completed</span>
                      <span className="font-semibold">{completionRate}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-600 rounded-full"
                        style={{ width: `${completionRate}%` }}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Cancelled</span>
                      <span className="font-semibold">{cancellationRate}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-red-600 rounded-full"
                        style={{ width: `${cancellationRate}%` }}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>No Show</span>
                      <span className="font-semibold">{noShowRate}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-orange-600 rounded-full"
                        style={{ width: `${noShowRate}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EventTypeAnalytics;
